// M3.5 — Sắp xếp thứ tự chủ đề (drag-drop)
// PRD R-P0-B14: admin có UI sắp xếp `topic_order` cho 3 bộ thưởng + bo_noi_quy
// Dùng HTML5 Drag-and-Drop API native (không thêm dependency).
//
// Ghi chú UX:
// - Mở drawer phải màn → list chủ đề trong bộ hiện tại
// - Kéo 1 hàng để đổi thứ tự → save = PATCH /question-topics/:bo/order
// - Bỏ qua các topic rỗng ('' hoặc null) — chúng không có thứ tự hiển thị
// - Chỉ ghi các topic đã được sort lại (giảm số row audit); server UPSERT idempotent

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { boLabel } from '../lib/bo.js';

export default function TopicOrderDialog({ open, bo, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // [{topic, cnt, sort_order}]
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !bo) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.topicsList(bo);
        // Lọc topic rỗng ra cuối list (hoặc bỏ — chúng không có sort meaning)
        const withTopic = (res.topics || []).filter((t) => t.topic && t.topic.trim());
        setItems(withTopic.map((t, i) => ({ ...t, sort_order: i })));
      } catch (err) {
        toast.error(err?.data?.error || err?.message || 'Không tải được chủ đề');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, bo]);

  if (!open) return null;

  function handleDragStart(idx) {
    return (e) => {
      setDragIdx(idx);
      e.dataTransfer.effectAllowed = 'move';
      // Safari cần setData để drag hoạt động
      try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
    };
  }
  function handleDragOver(idx) {
    return (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIdx === null || dragIdx === idx) return;
      // Reorder locally khi hover qua hàng khác
      const next = [...items];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      setItems(next);
      setDragIdx(idx);
    };
  }
  function handleDragEnd() {
    setDragIdx(null);
  }

  function moveUp(idx) {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setItems(next);
  }
  function moveDown(idx) {
    if (idx === items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setItems(next);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const order = items.map((t, i) => ({ topic: t.topic, sort_order: i }));
      await api.topicsReorder(bo, order);
      toast.success('Đã lưu thứ tự chủ đề');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40">
      <div className="bg-white w-full max-w-md shadow-2xl flex flex-col">
        <div className="px-5 py-3 border-b flex items-center">
          <h3 className="font-semibold">Sắp xếp chủ đề — {boLabel(bo)}</h3>
          <button onClick={onClose} disabled={saving}
                  className="ml-auto text-slate-400 hover:text-slate-800 disabled:opacity-40">✕</button>
        </div>

        <div className="p-4 text-sm text-slate-600 bg-slate-50 border-b">
          Kéo-thả hàng để đổi thứ tự. Thứ tự này dùng khi NV học flashcard tuần tự.
          {' '}Chủ đề chưa có (câu chưa gán topic) sẽ không xuất hiện ở đây.
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading && (
            <div className="p-8 text-center text-sm text-slate-400">Đang tải…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">
              Bộ này chưa có chủ đề nào. Hãy thêm câu hỏi có điền chủ đề trước.
            </div>
          )}
          {!loading && items.map((t, idx) => (
            <div key={t.topic}
                 draggable
                 onDragStart={handleDragStart(idx)}
                 onDragOver={handleDragOver(idx)}
                 onDragEnd={handleDragEnd}
                 className={`group flex items-center gap-2 px-3 py-2 mb-1 rounded border bg-white hover:bg-slate-50 cursor-move select-none ${
                   dragIdx === idx ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'
                 }`}>
              <span className="text-slate-400 group-hover:text-slate-600" title="Kéo để đổi thứ tự">⋮⋮</span>
              <span className="inline-block w-6 text-right text-xs text-slate-400 font-mono">{idx + 1}.</span>
              <span className="flex-1 text-slate-800 truncate" title={t.topic}>{t.topic}</span>
              <span className="text-xs text-slate-400">{t.cnt} câu</span>
              <button onClick={() => moveUp(idx)} disabled={idx === 0}
                      className="px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-200 rounded disabled:opacity-30">↑</button>
              <button onClick={() => moveDown(idx)} disabled={idx === items.length - 1}
                      className="px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-200 rounded disabled:opacity-30">↓</button>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t flex items-center gap-2 bg-slate-50">
          <div className="text-xs text-slate-500">{items.length} chủ đề</div>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} disabled={saving}
                    className="px-4 py-2 text-sm border rounded hover:bg-white disabled:opacity-40">
              Huỷ
            </button>
            <button onClick={handleSave} disabled={saving || loading || items.length === 0}
                    className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
              {saving ? 'Đang lưu…' : 'Lưu thứ tự'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
